/**
 * HolderChart — Holder count line/area chart using Lightweight Charts (TradingView).
 *
 * Owns its own data fetching via useHolderHistory hook.
 * Mirrors PriceChart structure: async dynamic import, resize observer, sample data badge.
 *
 * Story 8.3 — Task 3
 */

import type { HolderHistoryEntry, PriceInterval } from "@repo/shared-types";
import {
	ColorType,
	type IChartApi,
	type ISeriesApi,
	type SeriesOptionsMap,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";

import { useHolderHistory } from "../../hooks/api/use-holder-history";
import { toUTCTimestamp } from "../../lib/chart-utils";
import { formatCount } from "../../lib/format-number";
import { TimeRangeSelector } from "./TimeRangeSelector";

type HolderChartProps = {
	tokenId: string;
};

export function HolderChart({ tokenId }: HolderChartProps) {
	const [selectedInterval, setSelectedInterval] = useState<PriceInterval>("1D");
	const { data, isLoading, error, refetch } = useHolderHistory(tokenId, selectedInterval);
	const [chartReady, setChartReady] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<IChartApi | null>(null);
	const seriesRef = useRef<ISeriesApi<keyof SeriesOptionsMap> | null>(null);
	const observerRef = useRef<ResizeObserver | null>(null);

	// Mount/unmount chart — async import matching PriceChart pattern
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let cancelled = false;

		async function initChart() {
			const { createChart, AreaSeries } = await import("lightweight-charts");
			if (cancelled || !container) return;

			const chart: IChartApi = createChart(container, {
				width: container.clientWidth,
				height: 300,
				layout: {
					background: { type: ColorType.Solid, color: "transparent" },
					textColor: "#6b7280",
				},
				grid: {
					vertLines: { color: "rgba(100, 100, 100, 0.1)" },
					horzLines: { color: "rgba(100, 100, 100, 0.1)" },
				},
				rightPriceScale: {
					borderVisible: false,
				},
				timeScale: {
					borderVisible: false,
					timeVisible: selectedInterval === "1H" || selectedInterval === "4H",
				},
			});

			const series = chart.addSeries(AreaSeries, {
				lineColor: "#8b5cf6",
				topColor: "rgba(139, 92, 246, 0.3)",
				bottomColor: "rgba(139, 92, 246, 0.05)",
				priceFormat: {
					type: "custom",
					formatter: (value: number) => formatCount(value),
				},
			});

			chartRef.current = chart;
			seriesRef.current = series;

			const observer = new ResizeObserver((entries) => {
				const entry = entries[0];
				if (entry) {
					chart.applyOptions({ width: entry.contentRect.width });
				}
			});
			observer.observe(container);
			observerRef.current = observer;

			if (!cancelled) setChartReady(true);
		}

		void initChart();

		return () => {
			cancelled = true;
			observerRef.current?.disconnect();
			observerRef.current = null;
			if (chartRef.current) {
				chartRef.current.remove();
				chartRef.current = null;
				seriesRef.current = null;
			}
			setChartReady(false);
		};
	}, [selectedInterval]);

	// Update data — depends on chartReady to avoid race condition with async init
	useEffect(() => {
		const series = seriesRef.current;
		if (!series || !chartReady || !data?.data) return;

		const chartData = data.data.map((entry: HolderHistoryEntry) => ({
			time: toUTCTimestamp(entry.timestamp),
			value: entry.holderCount,
		}));

		series.setData(chartData);
		chartRef.current?.timeScale().fitContent();
	}, [data, chartReady]);

	const isSeeded = data?.meta?.dataSource === "seeded";

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<TimeRangeSelector selected={selectedInterval} onIntervalChange={setSelectedInterval} />
				{isSeeded && (
					<span className="text-xs text-pastel-muted bg-yellow-50 px-2 py-0.5 rounded">
						Sample Data
					</span>
				)}
			</div>

			{error ? (
				<div className="flex flex-col items-center justify-center h-[300px] text-pastel-muted text-sm gap-2">
					<p>Failed to load holder data</p>
					<button
						type="button"
						onClick={() => void refetch()}
						className="text-pastel-primary text-xs font-medium hover:underline"
					>
						Retry
					</button>
				</div>
			) : data?.data?.length === 0 && !isLoading ? (
				<div className="flex items-center justify-center h-[300px] text-pastel-muted text-sm">
					No holder data available
				</div>
			) : (
				<>
					{isLoading && !chartReady && <HolderChartSkeleton />}
					<div
						ref={containerRef}
						className="w-full h-[300px]"
						style={
							isLoading && !chartReady ? { visibility: "hidden", position: "absolute" } : undefined
						}
					/>
				</>
			)}
		</div>
	);
}

function HolderChartSkeleton() {
	return <div className="h-[300px] w-full rounded-xl bg-pastel-surface animate-pulse" />;
}
